import { useEffect, useState, MouseEvent, Dispatch, SetStateAction } from 'react';
import * as _ from 'lodash-es';
import { useSelector } from 'react-redux';
import ReactLoading from 'react-loading';
import css from 'styled-jsx/css';

import useDeviceScreen from 'lib/hooks/useDeviceScreen';

import ScrollingContainer from 'components/ScrollingContainer';
import ModelsList from 'components/Shared/MakeModelMenu/ModelsList';
import MakesList from 'components/Shared/MakeModelMenu/MakesList';
import { screen, colors } from 'components/styles/variables';

import State from 'types/State';
import Model from 'types/Model';
import Make from 'types/Make';

import { FilterFunction } from './types';

type DisabledProp = (model: Model) => boolean;

export interface MakeModelMenuProps {
  value?: MakeModelMenuValue;
  allMakesAndModelsOption?: boolean;
  onSelect: (make?: Make, model?: Model) => void;
  filter?: FilterFunction;
  disabled?: DisabledProp;
  setIsModalOpen?: Dispatch<SetStateAction<boolean>>;
  mobileHeight: 'full' | 'compact';
}

export interface MakeModelMenuValue {
  make?: string;
  model?: string;
}

export default function MakeModelMenu({
  value,
  onSelect,
  allMakesAndModelsOption,
  filter,
  disabled,
  setIsModalOpen,
  mobileHeight,
}: MakeModelMenuProps): JSX.Element {
  const makesAndModels = useSelector<State, Make[]>((state) => state.makesAndModels);

  const [selectedMake, setSelectedMake] = useState<Make | undefined>(() =>
    getMake(makesAndModels, value?.make)
  );
  const [activeList, setActiveList] = useState<'make' | 'model'>('make');
  useEffect(() => {
    setSelectedMake(() => getMake(makesAndModels, value?.make));
  }, [makesAndModels, value?.make]);

  const { isMobile } = useDeviceScreen();

  let scrollingContainerHeight;
  if (isMobile) {
    scrollingContainerHeight = mobileHeight === 'full' ? '100%' : 465;
  }

  function handleChoose(model: Model): void {
    onSelect(selectedMake, model);
    setIsModalOpen?.(false);
    setActiveList('make');
  }

  function handleShowMakes(event: MouseEvent): void {
    event.stopPropagation();
    setSelectedMake(undefined);
    setActiveList('make');
  }

  function onSelectMake(make: Make | undefined): void {
    setSelectedMake(make);
    setActiveList('model');
  }

  function onSelectAll(model: Model): void {
    onSelect(undefined, model);
    setIsModalOpen?.(false);
  }

  const scoped = css.resolve`
    .loading-container {
      margin: 25px auto;
    }

    .hidden {
      display: none;
    }

    @media (min-width: ${screen.mobileMax}) {
      .mm-scrolling-container {
        height: calc(70vh - 60px);
        max-height: 465px;
        min-height: 200px;
      }
    }
  `;

  const scrollingContainerProps = {
    className: `${scoped.className} mm-scrolling-container`,
    height: scrollingContainerHeight,
  };

  let content = null;

  if (activeList === 'make') {
    content = (
      <ScrollingContainer key="make" {...scrollingContainerProps}>
        <MakesList
          allMakesAndModelsOption={allMakesAndModelsOption}
          makes={makesAndModels}
          choose={onSelectMake}
          chooseAll={onSelectAll}
          className={`${scoped.className}`}
        />
      </ScrollingContainer>
    );
  }

  if (activeList === 'model' && selectedMake) {
    content = (
      <ScrollingContainer key="model" {...scrollingContainerProps}>
        <ModelsList
          // Filter models by a function passed in as a prop,
          // if none was passed, this will return all models
          models={_.filter(selectedMake?.models, filter) as Model[]}
          make={selectedMake}
          disabled={disabled}
          choose={handleChoose}
          allModelsOption={allMakesAndModelsOption}
          className={scoped.className}
          showMakes={handleShowMakes}
        />
      </ScrollingContainer>
    );
  }

  return (
    <div data-make-model-menu style={{ height: '100%' }}>
      <div className="body" style={{ height: '100%' }}>
        {_.isEmpty(makesAndModels) && (
          <ReactLoading
            type="spokes"
            color={colors.BLACK}
            width={40}
            height={40}
            className={`loading-container ${scoped.className}`}
          />
        )}

        {content}
      </div>
      {scoped.styles}
      <style jsx>
        {`
          .body {
            overflow: hidden;
            border-radius: 8px;
          }
        `}
      </style>
    </div>
  );
}

function getMake(makes?: Make[], name?: string): Make | undefined {
  if (!makes || !name) {
    return undefined;
  }

  return makes.find((make) => make.name === name);
}
